import {
  ClickableTableCell,
  ClickableTableRow,
} from '@/components/Table/ClickableTable'
import type { Id } from '@/infra/id'
import { type ColumnId } from '@/features/admin/bookkeeping/bookkeeping-manager-table-columns'
import { ImageCellContent } from '@/components/Table/TableCell'
import { Pill } from '@/components/Pill'
import { timeago } from '@/utils/date-util'

import styles from './BookkeepingManagerTableRow.module.css'

export interface BookkeepingManagerTableRowChampionPro {
  displayName: string
  avatar?: string | null
}

export interface BookkeepingManagerTableRowProps {
  id: Id
  columnIds: ColumnId[]
  isActive: boolean
  onPress: () => void
  jobShortId: string
  customerDisplayName?: string
  customerAvatar?: string | null
  jobName: string
  companyName?: string | null
  /**
   * `null` when no Pro is assigned.
   */
  championPro: BookkeepingManagerTableRowChampionPro | null
  proEmail?: string
  displayAccessMethod: string
  displayAction: string
  datePendingSince: Date
}

export function BookkeepingManagerTableRow({
  id,
  columnIds,
  isActive,
  onPress,
  jobShortId,
  customerDisplayName,
  customerAvatar,
  jobName,
  companyName,
  championPro,
  proEmail,
  displayAccessMethod,
  displayAction,
  datePendingSince,
}: BookkeepingManagerTableRowProps) {
  return (
    <ClickableTableRow
      className={styles.root}
      onClick={onPress}
      active={isActive}
      data-test={`bookkeeping-manager-table-row-${id}`}
    >
      {columnIds.map((columnId) => {
        switch (columnId) {
          case 'JobId':
            return <TextCell key={columnId} content={jobShortId} />

          case 'Customer':
            return (
              <ImageCell
                key={columnId}
                title={customerDisplayName}
                image={customerAvatar}
              />
            )

          case 'JobName':
            return <TextCell key={columnId} content={jobName} />
          case 'CompanyName':
            return <CompanyNameCell key={columnId} companyName={companyName} />

          case 'Pro':
            return <ChampionProCell key={columnId} championPro={championPro} />

          case 'ProEmail':
            return <TextCell key={columnId} content={proEmail} />

          case 'AccessMethod':
            return <TextCell key={columnId} content={displayAccessMethod} />

          case 'Action':
            return <ActionCell key={columnId} content={displayAction} />

          case 'TimeInAction':
            return <TimeInActionCell key={columnId} time={datePendingSince} />

          default:
            return <ClickableTableCell key={columnId} />
        }
      })}
    </ClickableTableRow>
  )
}

interface TextCellProps {
  content?: string
}

function TextCell({ content }: TextCellProps) {
  return (
    <ClickableTableCell className={styles.text}>
      <span>{content}</span>
    </ClickableTableCell>
  )
}

interface CompanyNameCellProps {
  companyName?: string | null
}

function CompanyNameCell({ companyName }: CompanyNameCellProps) {
  return (
    <ClickableTableCell className={styles.text}>
      <span>{companyName}</span>
    </ClickableTableCell>
  )
}

interface ImageCellProps {
  title?: string
  image?: string | null
}

function ImageCell({ title, image }: ImageCellProps) {
  return (
    <ClickableTableCell className={styles.text}>
      <ImageCellContent title={title} image={image} />
    </ClickableTableCell>
  )
}

interface ActionCellProps {
  content: string
}

function ActionCell({ content }: ActionCellProps) {
  return (
    <ClickableTableCell className={styles.text}>
      <Pill>{content}</Pill>
    </ClickableTableCell>
  )
}

interface TimeInActionCellProps {
  time: Date
}

function TimeInActionCell({ time }: TimeInActionCellProps) {
  const content = timeago(time, 'long')

  return <TextCell content={content} />
}

interface ChampionProCellProps {
  championPro: BookkeepingManagerTableRowChampionPro | null
}

function ChampionProCell({ championPro }: ChampionProCellProps) {
  if (!championPro) {
    return <TextCell content="" />
  }
  return (
    <ImageCell title={championPro.displayName} image={championPro.avatar} />
  )
}
